<script setup lang="ts">
import { ref, computed, watch, onUnmounted, unref } from 'vue'
import { storeToRefs } from 'pinia'
import { useWindowSize, useMediaQuery } from '@vueuse/core'
import { MediaQuery } from '@cplug/design-system/components/props'
import { DashboardLayoutTemplate } from '@cplug/design-system/components'
import { BaseFormModal, BaseDialogAlert } from '@cplug/frontend-core/components'
import { useLoadingIndicator } from '#imports'
import { useNavigationTour } from '~/domain/shared/tour/composables/useNavigationTour'
import { useAssistantSidebar } from '~/domain/shared/assistant/composables/useAssistantSidebar'
import AssistantSidebarPanel from '~/domain/shared/assistant/components/AssistantSidebarPanel.vue'
import MenuSidebar from '~/domain/shared/navigation/components/MenuSidebar.vue'
import BaseHeaderLeftSide from '~/domain/shared/navigation/components/BaseHeaderLeftSide.vue'
import BaseHeaderRightSide from '~/domain/shared/navigation/components/BaseHeaderRightSide.vue'
import BaseHeaderCenterSide from '~/domain/shared/navigation/components/BaseHeaderCenterSide.vue'
import StagingAccountActivationBanner from '~/domain/shared/staging-account-activation/components/molecules/StagingAccountActivationBanner.vue'
import TwoFactorBackupLoginBanner from '~/domain/shared/two-factor/components/molecules/TwoFactorBackupLoginBanner.vue'
import useMenuSidebarStore from '~/shared/stores/useMenuSidebarStore'
import useLayoutContentBreakpointStore from '~/shared/stores/useLayoutContentBreakpointStore'

useNavigationTour()

const isDesktop = useMediaQuery(MediaQuery.DesktopWithSidebar)
const { isLoading } = useLoadingIndicator()

const menuSidebarRef = ref<{ totalSidebarWidth?: number } | null>(null)
const menuSidebarStore = useMenuSidebarStore()
const layoutContentBreakpointStore = useLayoutContentBreakpointStore()
const { assistantSidebarWidth } = storeToRefs(menuSidebarStore)
const { width: viewportWidth } = useWindowSize()

const {
  isAssistantEnabled,
  isAssistantOpen,
  rightSidebarWidth,
  rightSidebarOverlay,
  shouldHideMenuForAssistant
} = useAssistantSidebar()

const sidebarWidth = computed(() => {
  if (shouldHideMenuForAssistant.value) {
    return 0
  }

  const width = unref(menuSidebarRef.value?.totalSidebarWidth)
  return typeof width === 'number' ? width : 200
})

const isLayoutDesktopWithSidebar = computed(() =>
  isDesktop.value && !shouldHideMenuForAssistant.value
)

const effectiveLeftSidebarWidth = computed(() => sidebarWidth.value)

watch(
  [viewportWidth, effectiveLeftSidebarWidth, assistantSidebarWidth],
  ([width, leftSidebarWidth, assistantWidth]) => {
    const contentWidth = Math.max(0, width - leftSidebarWidth - assistantWidth)
    layoutContentBreakpointStore.setContentWidth(contentWidth)

    document.documentElement.classList.toggle(
      'layout-content-compact',
      contentWidth < 1024 && assistantWidth > 0
    )
  },
  { immediate: true }
)

watch(
  [isAssistantOpen, rightSidebarWidth, rightSidebarOverlay, shouldHideMenuForAssistant],
  ([isOpen, width, overlay, hidesMenu]) => {
    const assistantWidth = isOpen && !overlay ? width : 0
    menuSidebarStore.setAssistantSidebarWidth(assistantWidth)
    menuSidebarStore.setAssistantHidesMenu(Boolean(hidesMenu))

    document.documentElement.classList.toggle('assistant-sidebar-open', Boolean(isOpen && !overlay))
    document.documentElement.classList.toggle('assistant-sidebar-overlay', Boolean(isOpen && overlay))
    document.documentElement.style.setProperty(
      '--assistant-sidebar-width',
      `${assistantWidth}px`
    )
  },
  { immediate: true }
)

watch(
  sidebarWidth,
  (width) => {
    menuSidebarStore.setTotalSidebarWidth(width)
    document.documentElement.style.setProperty('--layout-left-sidebar-width', `${width}px`)
  },
  { immediate: true }
)

onUnmounted(() => {
  menuSidebarStore.setAssistantSidebarWidth(0)
  menuSidebarStore.setAssistantHidesMenu(false)
  layoutContentBreakpointStore.reset()
  document.documentElement.classList.remove(
    'assistant-sidebar-open',
    'assistant-sidebar-overlay',
    'layout-content-compact'
  )
  document.documentElement.style.setProperty('--assistant-sidebar-width', '0px')
  document.documentElement.style.removeProperty('--layout-left-sidebar-width')
})
</script>

<template>
  <DashboardLayoutTemplate
    :sidebar-width="sidebarWidth"
    :is-desktop="isLayoutDesktopWithSidebar"
  >
    <template #sidebar>
      <MenuSidebar
        ref="menuSidebarRef"
        class="layout-persistent h-full w-full"
      />
    </template>

    <template #topbar-start>
      <BaseHeaderLeftSide />
    </template>

    <template #topbar-end>
      <BaseHeaderCenterSide />
      <BaseHeaderRightSide />
    </template>

    <!-- Conteúdo principal - o banner fica fora da área bloqueada para
         continuar clicável durante a transição de rota -->
    <div class="flex flex-1 flex-col">
      <StagingAccountActivationBanner class="px-4 pt-4 desktop:px-6" />
      <TwoFactorBackupLoginBanner class="px-4 pt-4 desktop:px-6" />
      <div
        class="relative flex flex-1 flex-col"
        :class="{ 'pointer-events-none opacity-60': isLoading }"
        :aria-busy="isLoading"
      >
        <slot />
        <div
          v-if="isLoading"
          class="absolute inset-0 z-10 bg-background/40"
          aria-hidden="true"
        />
      </div>
    </div>

    <template #modals>
      <!-- O DS não renderiza #right-sidebar. O painel fica aqui, fixo à direita. -->
      <aside
        v-if="isAssistantEnabled && isAssistantOpen"
        class="layout-right-sidebar fixed top-0 right-0 z-40 h-dvh overflow-hidden border-l border-sidebar-border bg-sidebar"
        :style="{ width: rightSidebarOverlay ? '100%' : `${rightSidebarWidth}px` }"
      >
        <AssistantSidebarPanel />
      </aside>
      <BaseDialogAlert />
      <BaseFormModal />
    </template>
  </DashboardLayoutTemplate>
</template>
